import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Navbar from './common/components/Navbar/Navbar';
import { useAuth } from './customHooks/useAuth';
import Routes from './routes/routes';
import theme from './theme';
import { useDiagramAuthRedirect } from './common/hooks/useDiagramAuthRedirect';

import './assets/styles/layout.css';
import Overlay from './common/components/Overlay';
import DiagramBpmnIndex from './modules/BpmnIoDiagram/Index';
import { closeDiagram } from './slices/diagramViewerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { DiagramViewerState } from './slices/diagramViewerInterface';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = useAuth();
  const isXPLR = location.pathname.includes('/design-nexus');

  // Detect diagram deep links and enforce authentication while preserving
  // the intended target so users can be redirected back after login.
  useDiagramAuthRedirect();

  const showNavbarOnRoutes = [
    '/industry',
    '/process',
    '/process-xplr',
    '/solution-xplr',
    '/ideation-xplr',
    '/portfolio',
    '/design-nexus',
    '/diagram',
    '/',
    '/enterprise-context',
    '/approval-requests',
    '/ideation-coi/:id',
    '/ideation-chatbot/:id',
    '/opportunity/:id',
    '/data-xplr',
    '/data-xplr/:id',
  ];

  const shouldShowNavbar =
    isAuthenticated &&
    showNavbarOnRoutes.some((route) => {
      if (route.includes(':')) {
        // convert pattern like '/ideation-coi/:id' to a regex
        const regex = new RegExp('^' + route.replace(':id', '[^/]+') + '$');
        return regex.test(location.pathname);
      }
      return location.pathname === route;
    });

  const dispatch = useDispatch();
  const { isOpen = false } = useSelector<RootState, DiagramViewerState>(
    (state) => state.diagramViewer
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(closeDiagram());
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100% - ${shouldShowNavbar ? '60px' : '0px'})`,
          marginTop: `${shouldShowNavbar ? '60px' : '0px'}`,
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {shouldShowNavbar && <Navbar />}

        <Box
          component="main"
          className={isXPLR ? 'no-scroll' : ''}
          sx={{
            flex: '0 1 auto',
            minHeight: 0,
            maxHeight: '100%',
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Toaster and Providers are now mounted once in main.tsx */}
          <Routes />
        </Box>
        <Overlay open={isOpen} onClose={() => dispatch(closeDiagram())} showCloseButton={false}>
          <DiagramBpmnIndex />
        </Overlay>
      </Box>
    </ThemeProvider>
  );
};

export default AppLayout;
